import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { api, ApiError, type AuthUser } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Recaptcha, RECAPTCHA_SITE_KEY } from '../lib/Recaptcha';

interface CustomerType {
  id: number;
  name: string;
}

interface CountyOption {
  id: number;
  name: string;
}

interface StateOption {
  id: number;
  name: string;
  abbreviation: string;
  counties: CountyOption[];
}

interface Country {
  id: number;
  name: string;
  abbreviation: string;
  stateLabel: string;
  countyLabel: string;
  states: StateOption[];
}

/** Title-case a county name for display (mirrors legacy `ucwords(strtolower())`). */
function titleCase(value: string): string {
  return value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

interface RegisterPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
  company?: string;
  country?: string;
  state?: string;
  city?: string;
  county?: string;
  zipCode?: string;
  customerType?: string;
  optInNewsletter: boolean;
  recaptchaToken?: string;
}

/**
 * Registration form — mirrors `.old/resources/views/auth/register.blade.php`
 * (form-floating fields in a 10-col centered row, mobile-paged via .page1/.page2).
 */
export function RegisterPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [form, setForm] = useState<RegisterPayload>({
    firstName: '', lastName: '', email: '', password: '',
    phone: '', company: '', country: '', state: '', city: '', county: '', zipCode: '',
    customerType: '', optInNewsletter: true,
  });
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState<1 | 2>(1);
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  // Load the customer types ("What role best describes you?") from the API —
  // mirrors the legacy `CustomerType::all()` populating the register dropdown.
  useEffect(() => {
    let active = true;
    api.get<CustomerType[]>('/api/v1/customer-types')
      .then((rows) => { if (active) setCustomerTypes(rows); })
      .catch(() => { /* non-fatal: dropdown simply stays empty */ });
    return () => { active = false; };
  }, []);

  // Load countries/states/counties for the cascading location dropdowns —
  // mirrors the legacy `$countries` tree behind x-forms.country/states/county.
  useEffect(() => {
    let active = true;
    api.get<Country[]>('/api/v1/countries')
      .then((rows) => { if (active) setCountries(rows); })
      .catch(() => { /* non-fatal: dropdowns simply stay empty */ });
    return () => { active = false; };
  }, []);

  const set = <K extends keyof RegisterPayload>(k: K, v: RegisterPayload[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // Cascading location selects: state list depends on country, county on state.
  const selectedCountry = countries.find((c) => c.name === form.country);
  const stateOptions = selectedCountry?.states ?? [];
  const selectedState = stateOptions.find((s) => s.name === form.state);
  const countyOptions = selectedState?.counties ?? [];

  // Reset dependent fields when a parent selection changes.
  const onCountryChange = (name: string) =>
    setForm((f) => ({ ...f, country: name, state: '', county: '' }));
  const onStateChange = (name: string) =>
    setForm((f) => ({ ...f, state: name, county: '' }));

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (form.password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setError('Please verify that you are human.');
      return;
    }
    setSubmitting(true);
    try {
      const me = await api.post<AuthUser>('/api/v1/auth/register', {
        ...form,
        recaptchaToken: recaptchaToken ?? undefined,
      });
      setUser(me);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) setError('That email is already registered. Try signing in.');
        else if (err.status === 400) setError('Please check the form and try again.');
        else setError(`Registration failed (${err.status}).`);
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Mobile: only show .page1 OR .page2 at a time, controlled by `page`.
  // Desktop (≥lg): both visible.
  const mobileHide = (which: 1 | 2) => (page === which ? 'd-block' : 'd-none d-lg-block');

  return (
    <div className="d-flex flex-column min-vh-100">
      <div className="navbar-custom topnav-navbar shadow-sm mb-1 clearfix">
        <div className="container-fluid d-flex d-lg-none justify-content-between">
          <div style={{ width: 65 }}>&nbsp;</div>
          <Link to="/" className="topnav-logo">
            <img src="/attribute/ads.png" alt="" style={{ height: 'auto', maxHeight: '5vh', minHeight: 70 }} />
          </Link>
          <div style={{ width: 65 }}>&nbsp;</div>
        </div>
        <div className="container-fluid d-none d-lg-block">
          <Link to="/" className="topnav-logo">
            <span className="topnav-logo-lg">
              <img src="/attribute/ads.png" alt="" height={70} />
            </span>
          </Link>
          <Link to="/" className="topnav-text">
            <span>FlexStorm Project Planner</span>
          </Link>
        </div>
      </div>

      <div className="container-fluid flex-fill">
        <div className="row justify-content-center text-center pt-3">
          <div className="col-lg-10 col-md-12">
            <h2>Create Your Account</h2>
          </div>
          <div className="col-lg-10 col-md-12">
            <p>Save, share, and manage multiple projects all in one easy to use app. All fields are required.</p>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate data-disable-unsaved-alert>
          <div className="row justify-content-center">
            {error && (
              <div className="col-lg-10 col-md-12">
                <div className="alert alert-danger">{error}</div>
              </div>
            )}

            <div className={`col-lg-10 col-md-12 ${mobileHide(1)}`}>
              <Floating id="emailaddress" label="Email Address*">
                <input className="form-control" type="email" id="emailaddress" autoComplete="email" required placeholder="Email Address*"
                  value={form.email} onChange={(e) => set('email', e.target.value)} />
              </Floating>
            </div>

            <div className={`col-lg-5 col-md-12 ${mobileHide(1)}`}>
              <Floating id="password" label="Password*">
                <input type="password" id="password" autoComplete="new-password" className="form-control" placeholder="Password" required minLength={8}
                  value={form.password} onChange={(e) => set('password', e.target.value)} />
              </Floating>
            </div>
            <div className={`col-lg-5 col-md-12 ${mobileHide(1)}`}>
              <Floating id="password_confirmation" label="Please enter your password again*">
                <input type="password" id="password_confirmation" autoComplete="new-password" className="form-control" placeholder="Confirm password" required minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </Floating>
            </div>

            <div className={`col-lg-5 col-md-12 ${mobileHide(1)}`}>
              <Floating id="fname" label="First Name*">
                <input type="text" className="form-control" id="fname" placeholder="First Name*" required
                  value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
              </Floating>
            </div>
            <div className={`col-lg-5 col-md-12 ${mobileHide(1)}`}>
              <Floating id="lname" label="Last Name*">
                <input type="text" className="form-control" id="lname" placeholder="Last Name*" required
                  value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
              </Floating>
            </div>

            <div className={`col-lg-5 col-md-12 ${mobileHide(1)}`}>
              <Floating id="phone" label="Phone Number*">
                <input type="tel" className="form-control" id="phone" placeholder="Phone Number*" required
                  value={form.phone} onChange={(e) => set('phone', e.target.value)} />
              </Floating>
            </div>

            <div className={`col-lg-5 col-md-12 ${mobileHide(2)}`}>
              <Floating id="company" label="Company Name*">
                <input type="text" className="form-control" id="company" placeholder="Company Name*" required
                  value={form.company} onChange={(e) => set('company', e.target.value)} />
              </Floating>
            </div>

            <div className={`col-lg-10 col-md-12 ${mobileHide(2)}`}>
              <div className="mb-2">
                <select id="country" className="form-select mb-2" required
                  value={form.country} onChange={(e) => onCountryChange(e.target.value)}>
                  <option value="" disabled hidden>Country*</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`col-lg-5 col-12 ${mobileHide(2)}`}>
              <div className="mb-2">
                <select id="state" className="form-select mb-2" required
                  value={form.state} onChange={(e) => onStateChange(e.target.value)}
                  disabled={!selectedCountry}>
                  <option value="" disabled hidden>State/Province*</option>
                  {stateOptions.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`col-lg-5 col-md-12 ${mobileHide(2)}`}>
              <Floating id="city" label="Area/City*">
                <input type="text" className="form-control" id="city" placeholder="Area/City*" required
                  value={form.city} onChange={(e) => set('city', e.target.value)} />
              </Floating>
            </div>

            <div className={`col-lg-5 col-12 ${mobileHide(2)}`}>
              <div className="mb-2">
                {selectedState && countyOptions.length === 0 ? (
                  <select id="county" className="form-select mb-2" value="NONE" disabled>
                    <option value="NONE">None Available</option>
                  </select>
                ) : (
                  <select id="county" className="form-select mb-2" required
                    value={form.county} onChange={(e) => set('county', e.target.value)}
                    disabled={!selectedState}>
                    <option value="" disabled hidden>County*</option>
                    {countyOptions.map((co) => (
                      <option key={co.id} value={co.name}>{titleCase(co.name)}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className={`col-lg-5 col-md-12 ${mobileHide(2)}`}>
              <Floating id="zipcode" label="Zip Code*">
                <input type="text" className="form-control" id="zipcode" placeholder="Zip Code*" required
                  value={form.zipCode} onChange={(e) => set('zipCode', e.target.value)} />
              </Floating>
            </div>

            <div className={`col-lg-10 col-md-12 ${mobileHide(2)}`}>
              <div className="mb-2">
                <select id="customertype" className="form-select mb-2" required
                  value={form.customerType} onChange={(e) => set('customerType', e.target.value)}>
                  <option value="" disabled hidden>What role best describes you?*</option>
                  {customerTypes.map((ct) => (
                    <option key={ct.id} value={ct.name}>{ct.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`col-lg-10 py-1 col-md-12 ${mobileHide(2)}`}>
              <div className="mb-2">
                <div className="form-check">
                  <input type="checkbox" className="form-check-input" id="checkbox-signup"
                    checked={form.optInNewsletter}
                    onChange={(e) => set('optInNewsletter', e.target.checked)} />
                  <label className="form-check-label" htmlFor="checkbox-signup">
                    Sign me up to receive the latest news and product information.
                  </label>
                </div>
              </div>
            </div>

            <div className={`col-lg-5 col-md-12 ${mobileHide(2)}`}>
              <Recaptcha
                className="mb-2"
                onVerify={(token) => setRecaptchaToken(token)}
                onExpire={() => setRecaptchaToken(null)}
              />
            </div>

            <div className={`col-lg-5 col-md-12 text-center text-md-end ${mobileHide(2)}`}>
              <button id="submitbutton" className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Creating account…' : 'Create Account'}
              </button>
            </div>

            {page === 1 && (
              <div className="col-12 mb-3 text-center d-block d-lg-none">
                <button type="button" className="btn btn-primary" onClick={() => setPage(2)}>
                  Continue
                </button>
              </div>
            )}
          </div>

          <div className="row mt-1">
            <div className="col-12 text-center">
              <p className="text-muted">
                Already have account?{' '}
                <Link to="/login" className="text-muted ms-1">
                  <b>Log In</b>
                </Link>
              </p>
            </div>
          </div>
        </form>
      </div>

      <footer className="footer footer-alt mt-auto">
        <div className="container-fluid text-center">
          <span className="lh-lg">
            &copy; {new Date().getFullYear()} Advanced Drainage Systems&trade;. All&nbsp;rights&nbsp;reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}

function Floating({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="form-floating mb-2">
      {children}
      <label htmlFor={id}>{label}</label>
    </div>
  );
}
